import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import TextField, { type TextFieldProps } from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type ReactNode } from 'react';
import { Controller, type Control, type FieldValues, type Path } from 'react-hook-form';

interface Props<TFieldValues extends FieldValues> extends Omit<
  TextFieldProps,
  'name' | 'label' | 'error'
> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  label: string;
  optionalLabel?: string;
  hint?: string;
  /** Reformats the value when the field loses focus (CEP). */
  formatOnBlur?: (value: string) => string;
  onBlurValue?: (value: string) => void;
  children?: ReactNode;
}

/**
 * Labelled, controlled text input with the field error underneath, for any form (same anatomy as
 * the domain `*FormField`s). Used by shared field groups such as `AddressFields`.
 */
const FormTextField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  optionalLabel,
  hint,
  formatOnBlur,
  onBlurValue,
  children,
  ...textFieldProps
}: Props<TFieldValues>) => (
  <Controller
    name={name}
    control={control}
    render={({ field, fieldState }) => (
      <FormControl fullWidth>
        <FormLabel htmlFor={name}>
          {label}
          {optionalLabel ? (
            <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
              {optionalLabel}
            </Typography>
          ) : null}
        </FormLabel>
        <TextField
          {...textFieldProps}
          {...field}
          id={name}
          fullWidth
          error={Boolean(fieldState.error)}
          helperText={fieldState.error?.message ?? hint}
          onBlur={() => {
            const value = String(field.value ?? '');
            const formatted = formatOnBlur && value ? formatOnBlur(value) : value;
            if (formatted !== value) field.onChange(formatted);
            field.onBlur();
            onBlurValue?.(formatted);
          }}
        >
          {children}
        </TextField>
      </FormControl>
    )}
  />
);

export default FormTextField;
